import { Link } from 'react-router-dom';
import { cities } from '../data/citiesData';
import { formatILS, formatPercent } from '../utils/format';
import RiskBadge from '../components/ui/RiskBadge';
import ScoreBar from '../components/ui/ScoreBar';
import EmailCapture from '../components/ui/EmailCapture';
import imgLiving from '../assets/images/interior-living.jpg';
import imgBedroom from '../assets/images/interior-bedroom.jpg';
import imgKitchen from '../assets/images/interior-kitchen.jpg';
import imgBalcony from '../assets/images/sea-view-balcony.jpg';
import './Home.css';


const HERO_STATS = [
  { value: "70 000 ₪", label: "Prix moyen/m² à Tel Aviv", note: "≈ 17 500 €/m²" },
  { value: "3–5 %", label: "Rendement locatif brut moyen", note: "Selon la ville" },
  { value: "+180 %", label: "Hausse des prix sur 10 ans", note: "Indice national" },
  { value: "~500 000", label: "Francophones en Israël", note: "Communauté établie" },
];

const FEATURES = [
  { num: "01", title: "Prix par ville", desc: "Prix au m², fourchettes de marché et tendances de fond." },
  { num: "02", title: "Rendements locatifs", desc: "Location classique longue durée et courte durée." },
  { num: "03", title: "Simulateurs", desc: "Calculez votre rendement brut en quelques secondes." },
  { num: "04", title: "Stratégies d'investissement", desc: "Trois profils types avec chiffres réels et analyse des risques." },
  { num: "05", title: "Accompagnement", desc: "Mise en relation avec des partenaires francophones locaux." },
];

export default function Home() {
  const enrichedCities = cities.map(c => {
    const avg = c.rentalYields.reduce((a, b) => a + b.grossYield, 0) / c.rentalYields.length;
    return { ...c, avgYield: avg };
  });

  return (
    <div>
      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="hero hero-grid hero-premium">
        <div className="container">
          <div className="hero-content">
            <span className="gold-tag">Investissement immobilier · Israël</span>
            <h1 style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)', lineHeight: 1.15 }}>
              Les meilleurs deals<br /><span className="hero-accent">immobiliers en Israël</span>
            </h1>
            <p className="hero-subtitle">
              Prix au m², rendements réels, opportunités de marché — tout ce qu'il faut pour trouver le bon deal immobilier en Israël, en tant que francophone.
            </p>
            <div className="hero-actions">
              <Link to="/accompagnement" className="btn btn-accent btn-lg">Trouver mon deal</Link>
              <Link to="/villes" className="btn btn-outline-white btn-lg">Comparer les villes</Link>
            </div>
          </div>

          {/* Stat bar inside hero */}
          <div className="hero-stat-bar">
            {HERO_STATS.map((s, i) => (
              <div key={i} className="hero-stat-item">
                <span className="hero-stat-value">{s.value}</span>
                <span className="hero-stat-label">{s.label}</span>
                <span className="hero-stat-note">{s.note}</span>
              </div>
            ))}
          </div>

          {/* Trusted by bar */}
          <div style={{ borderTop: '1px solid rgba(196,163,90,0.15)', padding: '1rem 0', display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(250,250,248,0.4)' }}>Utilisé par des investisseurs de</span>
            {['France', 'Belgique', 'Suisse', 'Canada', 'Maroc'].map(c => (
              <span key={c} style={{ fontSize: '0.82rem', fontWeight: 600, color: 'rgba(250,250,248,0.55)', letterSpacing: '0.04em' }}>{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cities comparison table ──────────────────────────────── */}
      <section className="section-light">
        <div className="container">
          <div className="section-header">
            <span className="gold-tag">Comparatif de marché</span>
            <h2>Les marchés immobiliers en un tableau</h2>
            <p>Prix au m², rendements bruts et profil de risque des {cities.length} principales villes d'Israël</p>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Ville</th>
                  <th>Région</th>
                  <th>Prix moy./m²</th>
                  <th>Rendement brut</th>
                  <th>Attractivité</th>
                  <th>Risque</th>
                  <th>Fiche</th>
                </tr>
              </thead>
              <tbody>
                {enrichedCities.map(city => (
                  <tr key={city.id}>
                    <td>
                      <strong style={{ fontFamily: 'var(--font-display)', color: 'var(--color-primary)' }}>{city.name}</strong>
                    </td>
                    <td><span className="badge badge-blue">{city.region}</span></td>
                    <td><strong>{formatILS(city.averagePricePerSqm)}</strong></td>
                    <td>
                      <span style={{ fontWeight: 700, color: city.avgYield >= 4 ? 'var(--color-success)' : 'var(--color-primary)' }}>
                        {formatPercent(city.avgYield)}
                      </span>
                    </td>
                    <td>
                      <div style={{ minWidth: 100 }}>
                        <ScoreBar score={city.attractivenessScore} />
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{city.attractivenessScore}/10</span>
                      </div>
                    </td>
                    <td><RiskBadge level={city.riskLevel} /></td>
                    <td>
                      <Link to={`/villes/${city.slug}`} className="btn btn-primary btn-sm">Voir</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="insight-box" style={{ marginTop: '1.5rem' }}>
            Données indicatives 2024–2025. Rendements bruts avant charges, frais de gestion et fiscalité. Consultez un professionnel pour une analyse personnalisée.
          </div>
        </div>
      </section>

      {/* ── Why section ──────────────────────────────────────────── */}
      <section className="section-white">
        <div className="container">
          <div className="why-grid">
            <div>
              <span className="gold-tag">Notre approche</span>
              <h2>Trouver le bon deal, pas juste un bien</h2>
              <p style={{ fontSize: '1.05rem', lineHeight: 1.9, color: 'var(--color-text)' }}>
                Un bon deal immobilier en Israël, c'est le bon prix, dans la bonne ville, au bon moment. Deal Israel est conçu pour les francophones qui veulent aller à l'essentiel : comparer les marchés, calculer leur rendement réel et identifier les opportunités — sans se noyer dans la complexité.
              </p>
              <div className="insight-box">
                Données indépendantes, outils concrets, zéro commission. Notre seul objectif : vous aider à décider.
              </div>
              <Link to="/accompagnement" className="btn btn-primary" style={{ marginTop: '1.5rem' }}>Discuter de mon projet</Link>
            </div>
            <div className="why-features">
              {FEATURES.map((f, i) => (
                <div key={i} className="feature-item">
                  <span className="feature-num">{f.num}</span>
                  <div>
                    <strong style={{ color: 'var(--color-primary)', fontSize: '0.95rem' }}>{f.title}</strong>
                    <p style={{ margin: 0, fontSize: '0.875rem', marginTop: '0.2rem' }}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Nos outils ───────────────────────────────────────────── */}
      <section className="section-light">
        <div className="container">
          <div className="section-header">
            <span className="gold-tag">Outils Deal Israel</span>
            <h2>Analysez votre deal en quelques minutes</h2>
          </div>
          <div className="grid-3">
            {[
              { title: 'Profil investisseur', desc: 'Découvrez la ville et la stratégie adaptées à votre projet en 4 questions.', href: '/profil-investisseur', label: 'Démarrer le quiz', featured: true },
              { title: 'Comparer des villes', desc: 'Comparaison côte à côte de jusqu\'à 3 villes sur tous les indicateurs clés.', href: '/comparateur', label: 'Comparer', featured: true },
              { title: 'Calculateur net', desc: 'Rendement après Arnona, taxes, gestion et impôts.', href: '/calculateur-net', label: 'Calculer' },
              { title: 'Prix par ville', desc: 'Analyse complète par ville et par quartier pour chaque marché.', href: '/villes', label: 'Explorer' },
              { title: 'Guide & Articles', desc: 'Fiscalité, juridique, stratégies — en français.', href: '/blog', label: 'Lire' },
              { title: 'Simulateurs bruts', desc: 'Location classique et courte durée en 30 secondes.', href: '/simulateurs', label: 'Simuler' },
            ].map((tool, i) => (
              <Link key={i} to={tool.href} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '1.75rem', background: tool.featured ? 'rgba(196,163,90,0.05)' : 'var(--color-bg-white)', border: `${tool.featured ? '1.5px' : '1px'} solid ${tool.featured ? 'rgba(196,163,90,0.35)' : 'var(--color-border)'}`, borderRadius: 'var(--radius-lg)', textDecoration: 'none', color: 'inherit', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderLeftColor = 'var(--color-accent)'; e.currentTarget.style.borderLeftWidth = '3px'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderLeftColor = tool.featured ? 'rgba(196,163,90,0.35)' : 'var(--color-border)'; e.currentTarget.style.borderLeftWidth = tool.featured ? '1.5px' : '1px'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                {tool.featured && <span style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--color-accent)', fontWeight: 700 }}>Nouveau</span>}
                <strong style={{ color: 'var(--color-primary)', fontSize: '1rem' }}>{tool.title}</strong>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{tool.desc}</p>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 'auto' }}>{tool.label} →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── City preview cards ───────────────────────────────────── */}
      <section className="section-light">
        <div className="container">
          <div className="section-header">
            <span className="gold-tag">Sélection</span>
            <h2>Trois marchés à retenir</h2>
            <p>Prix, rendements et profil de risque des villes les plus suivies</p>
          </div>
          <div className="grid-3">
            {cities.slice(0, 3).map(city => (
              <Link to={`/villes/${city.slug}`} key={city.id} className="city-preview-card">
                <div className="city-preview-header">
                  <div>
                    <h3 className="city-preview-name">{city.name}</h3>
                    <span className="city-preview-region">{city.region}</span>
                  </div>
                  <RiskBadge level={city.riskLevel} />
                </div>
                <div className="city-preview-stats">
                  <div>
                    <span className="city-preview-stat-value">{formatILS(city.averagePricePerSqm)}</span>
                    <span className="city-preview-stat-sub">/m²</span>
                  </div>
                  <div>
                    <span className="city-preview-stat-value">{formatPercent(city.rentalYields[1]?.grossYield)}</span>
                    <span className="city-preview-stat-sub"> rendement</span>
                  </div>
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.6 }}>{city.shortComment}</p>
                <span className="city-preview-cta">Voir la fiche</span>
              </Link>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/villes" className="btn btn-outline">Voir toutes les villes ({cities.length})</Link>
          </div>
        </div>
      </section>

      {/* ── Pourquoi nous faire confiance ────────────────────────── */}
      <section className="section-white">
        <div className="container">
          <div className="section-header">
            <span className="gold-tag">Transparence</span>
            <h2>Un site indépendant et sans conflit d'intérêt</h2>
          </div>
          <div className="grid-3">
            {[
              {
                svg: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,
                title: 'Données publiques',
                desc: 'Toutes les données de prix et rendements sont issues de sources publiques israéliennes (Tabu, CBS, relevés de marché). Elles sont indicatives.'
              },
              {
                svg: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
                title: 'Pas de commission',
                desc: 'Deal Israel ne perçoit aucune commission sur les transactions. Notre rôle est informatif. Consultez toujours un professionnel avant d\'investir.'
              },
              {
                svg: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
                title: 'Mis à jour régulièrement',
                desc: 'Les données sont revues régulièrement pour refléter l\'évolution du marché. Dernière mise à jour : mars 2025.'
              },
            ].map((item, i) => (
              <div key={i} style={{ background: 'var(--color-bg)', borderRadius: 'var(--radius-lg)', padding: '1.75rem', border: '1px solid var(--color-border)' }}>
                <div style={{ color: 'var(--color-accent)', marginBottom: '1rem' }}>{item.svg}</div>
                <h4 style={{ color: 'var(--color-primary)', marginBottom: '0.5rem' }}>{item.title}</h4>
                <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.75, color: 'var(--color-text)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Photo gallery ────────────────────────────────────────── */}
      <section className="section-white">
        <div className="container">
          <div className="section-header">
            <span className="gold-tag">Immobilier premium</span>
            <h2>Le parc immobilier israélien</h2>
            <p>Des résidences modernes avec vue mer aux appartements rénovés en centre-ville.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gridTemplateRows: '220px 220px', gap: '0.75rem', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <div style={{ gridRow: '1 / 3', backgroundImage: `url(${imgBalcony})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 'var(--radius-md)' }} />
            <div style={{ backgroundImage: `url(${imgLiving})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 'var(--radius-md)' }} />
            <div style={{ backgroundImage: `url(${imgBedroom})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 'var(--radius-md)' }} />
            <div style={{ backgroundImage: `url(${imgKitchen})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 'var(--radius-md)' }} />
            <div style={{ backgroundImage: `url(${imgLiving})`, backgroundSize: 'cover', backgroundPosition: 'center 30%', borderRadius: 'var(--radius-md)', filter: 'brightness(0.85)' }} />
          </div>
          <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
            Exemples de biens — résidences bord de mer, appartements vue mer, intérieurs rénovés
          </p>
        </div>
      </section>

      {/* ── Email capture ────────────────────────────────────────── */}
      <EmailCapture />

      {/* ── CTA final ────────────────────────────────────────────── */}
      <section className="section-cta">
        <div className="container">
          <div className="cta-inner">
            <div>
              <span className="gold-tag">Passez à l'action</span>
              <h2 style={{ color: '#fafaf8', marginTop: '0.5rem' }}>Votre prochain deal commence ici</h2>
              <p style={{ color: 'rgba(250,250,248,0.72)', marginBottom: 0, fontSize: '1.05rem' }}>
                Décrivez votre projet en 2 minutes. Nous vous aidons à identifier les meilleures opportunités du marché israélien.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Link to="/accompagnement" className="btn btn-accent btn-lg">Trouver mon deal</Link>
              <Link to="/simulateurs" className="btn btn-outline-white">Calculer mon rendement</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
